/**
 * Helpers for the signature block inside a quote email body.
 *
 * A body is laid out as `<user content><SIGNATURE_PREFIX><signature html>`, so
 * everything from the first prefix onwards is the signature. Keeping that the
 * only source of truth means no caller has to track which signature is applied:
 * a body is enough to answer "is one there?", "what did the user write?" and
 * "what does it look like with a different signature?".
 */

/** SIGNATURE_PREFIX marks where the signature block starts within an email body. */
export const SIGNATURE_PREFIX = '____';

const SIGNATURE_SEPARATOR = '<br><br>';

/**
 * Blank markup left between the user's text and the signature block. The
 * separator only survives as literal `<br>`s until Quill reformats the body into
 * paragraphs, and the prefix ends up inside a paragraph of its own, so cutting
 * at the prefix leaves empty paragraphs and a dangling opening tag behind.
 * Without trimming those, every signature swap pushes the signature further down.
 */
const TRAILING_BLANK_MARKUP = /(?:\s|<br\s*\/?>|<p[^>]*>(?:\s|<br\s*\/?>)*(?:<\/p>)?)*$/i;

/** hasSignature reports whether `body` already carries a signature block. */
export const hasSignature = (body: string): boolean => body.includes(SIGNATURE_PREFIX);

/** stripSignature returns everything the user wrote, without the signature block. */
export const stripSignature = (body: string): string => {
  if (!hasSignature(body)) {
    return body;
  }
  return body.split(SIGNATURE_PREFIX)[0].replace(TRAILING_BLANK_MARKUP, '');
};

/** signatureBlock renders `signature` as the trailing block of an email body. */
export const signatureBlock = (signature: string): string =>
  `${SIGNATURE_SEPARATOR}${SIGNATURE_PREFIX}${signature}`;

/**
 * applySignature returns `body` with its signature block replaced by
 * `signature`, preserving whatever the user wrote above it.
 */
export const applySignature = (body: string, signature: string): string =>
  `${stripSignature(body)}${signatureBlock(signature)}`;
